import { LinkType } from './linkTypes';
import { isAmazonMusicAddress, isAmazonMusicHost } from './amazonMusicUrl';
import { normalizeLinkHost } from './outsideLinks';
import type { MusicService } from '../music-services/types';
import { amazonMusic } from '../music-services/services/AmazonMusic';
import { appleMusic } from '../music-services/services/AppleMusic';
import { audiomack } from '../music-services/services/Audiomack';
import { audius } from '../music-services/services/Audius';
import { boomplay } from '../music-services/services/Boomplay';
import { deezer } from '../music-services/services/Deezer';
import { gaana } from '../music-services/services/Gaana';
import { jiosaavn } from '../music-services/services/JioSaavn';
import { napster } from '../music-services/services/Napster';
import { pandora } from '../music-services/services/Pandora';
import { qobuz } from '../music-services/services/Qobuz';
import { resso } from '../music-services/services/Resso';
import { soundcloud } from '../music-services/services/SoundCloud';
import { spotify } from '../music-services/services/Spotify';
import { tidal } from '../music-services/services/Tidal';
import { vkMusic } from '../music-services/services/VKMusic';
import { yandexMusic } from '../music-services/services/YandexMusic';
import { youtube } from '../music-services/services/YouTube';
import { youtubeMusic } from '../music-services/services/YouTubeMusic';
import { zvuk } from '../music-services/services/Zvuk';

/**
 * The label of a link button on a SmartLink page (owner decision 7, 2026-10-20). A button never
 * has an empty text.
 *
 * - `title`: the artist typed a title. It wins.
 * - `service`: a known music or video service. The renderer shows "Listen on <Service>" or
 *   "Watch on <Service>".
 * - `host`: any other address. The renderer shows the host with no `www.`, for example
 *   "laziibee.com".
 * - `none`: no address that the rule can read. The renderer shows a generic "Open".
 *
 * Pure code: the renderers own the words, so each one can translate them.
 */
export type LinkButtonLabel =
  | { kind: 'title'; text: string }
  | { kind: 'service'; verb: 'listen' | 'watch'; serviceName: string }
  | { kind: 'host'; host: string }
  | { kind: 'none' };

export interface LinkButtonLabelInput {
  title?: string | null;
  url?: string | null;
  linkType?: LinkType | string | null;
}

const nameOf = (service: MusicService): string => service.shortName;

/**
 * The button name of each music and video service. The names come from the fym-shared service
 * metadata. QQ Music has a Chinese short name there, so it gets a Latin name here. Anghami has no
 * service entry any more (retired), but old links can still have its type.
 */
const SERVICE_NAMES: Partial<Record<LinkType, string>> = {
  [LinkType.amazonmusic]: nameOf(amazonMusic),
  [LinkType.anghami]: 'Anghami',
  [LinkType.applemusic]: nameOf(appleMusic),
  [LinkType.audiomack]: nameOf(audiomack),
  [LinkType.audius]: nameOf(audius),
  [LinkType.boomplay]: nameOf(boomplay),
  [LinkType.deezer]: nameOf(deezer),
  [LinkType.gaana]: nameOf(gaana),
  [LinkType.jiosaavn]: nameOf(jiosaavn),
  [LinkType.napster]: nameOf(napster),
  [LinkType.pandora]: nameOf(pandora),
  [LinkType.qobuz]: nameOf(qobuz),
  [LinkType.qqmusic]: 'QQ Music',
  [LinkType.resso]: nameOf(resso),
  [LinkType.soundcloud]: nameOf(soundcloud),
  [LinkType.spotify]: nameOf(spotify),
  [LinkType.tidal]: nameOf(tidal),
  [LinkType.vkmusic]: nameOf(vkMusic),
  [LinkType.yandexmusic]: nameOf(yandexMusic),
  [LinkType.youtube]: nameOf(youtube),
  [LinkType.youtubemusic]: nameOf(youtubeMusic),
  [LinkType.zvuk]: nameOf(zvuk),
};

/** The only video service. YouTube Music is a music service, so its verb is "listen". */
const VIDEO_SERVICES: readonly LinkType[] = [LinkType.youtube];

/**
 * The hosts that identify a service when the link type is generic or missing. The first match
 * wins, so `music.youtube.com` comes before `youtube.com`. A subdomain of a host matches too.
 * `vk.com` is not here: most VK links are a profile, not music.
 */
const SERVICE_HOSTS: readonly { host: string; type: LinkType }[] = [
  { host: 'music.youtube.com', type: LinkType.youtubemusic },
  { host: 'youtube.com', type: LinkType.youtube },
  { host: 'youtu.be', type: LinkType.youtube },
  { host: 'spotify.com', type: LinkType.spotify },
  { host: 'spotify.link', type: LinkType.spotify },
  { host: 'music.apple.com', type: LinkType.applemusic },
  { host: 'deezer.com', type: LinkType.deezer },
  { host: 'deezer.page.link', type: LinkType.deezer },
  { host: 'tidal.com', type: LinkType.tidal },
  { host: 'soundcloud.com', type: LinkType.soundcloud },
  { host: 'pandora.com', type: LinkType.pandora },
  { host: 'napster.com', type: LinkType.napster },
  { host: 'qobuz.com', type: LinkType.qobuz },
  { host: 'y.qq.com', type: LinkType.qqmusic },
  { host: 'music.yandex.ru', type: LinkType.yandexmusic },
  { host: 'music.yandex.com', type: LinkType.yandexmusic },
  { host: 'zvuk.com', type: LinkType.zvuk },
  { host: 'gaana.com', type: LinkType.gaana },
  { host: 'jiosaavn.com', type: LinkType.jiosaavn },
  { host: 'saavn.com', type: LinkType.jiosaavn },
  { host: 'resso.com', type: LinkType.resso },
  { host: 'boomplay.com', type: LinkType.boomplay },
  { host: 'boomplaymusic.com', type: LinkType.boomplay },
  { host: 'audiomack.com', type: LinkType.audiomack },
  { host: 'audius.co', type: LinkType.audius },
  { host: 'anghami.com', type: LinkType.anghami },
];

/** The host (lower case, no `www.`) and the path of a web address. Null when the address is not http(s). */
function parseLinkAddress(url: string | null | undefined): { host: string; path: string } | null {
  const value = (url ?? '').trim();
  if (value === '') {
    return null;
  }
  const hasScheme = /^[a-z][a-z0-9+.-]*:/i.test(value);
  if (hasScheme && !/^https?:/i.test(value)) {
    return null;
  }
  try {
    const parsed = new URL(hasScheme ? value : `https://${value}`);
    const host = normalizeLinkHost(parsed.hostname);
    return host === '' ? null : { host, path: parsed.pathname };
  } catch {
    return null;
  }
}

/** The host of a web address in lower case with no `www.`. Null when the address is not http(s). */
export function linkHost(url: string | null | undefined): string | null {
  return parseLinkAddress(url)?.host ?? null;
}

const matchesHost = (host: string, domain: string): boolean => host === domain || host.endsWith(`.${domain}`);

/** The service of a host, or null when the host is not a known music or video service. */
export function serviceLinkTypeForHost(host: string): LinkType | null {
  if (isAmazonMusicHost(host)) {
    return LinkType.amazonmusic;
  }
  return SERVICE_HOSTS.find((entry) => matchesHost(host, entry.host))?.type ?? null;
}

/**
 * The service of a web address, or null. It reads the path too: `amazon.co.jp/music/...` is
 * Amazon Music, while `amazon.co.jp/dp/...` is the shop.
 */
export function serviceLinkTypeForUrl(url: string | null | undefined): LinkType | null {
  const address = parseLinkAddress(url);
  if (address === null) {
    return null;
  }
  if (isAmazonMusicAddress(address.host, address.path)) {
    return LinkType.amazonmusic;
  }
  return serviceLinkTypeForHost(address.host);
}

const isLinkType = (value: string): value is LinkType => (Object.values(LinkType) as string[]).includes(value);

/** Applies owner decision 7. See `LinkButtonLabel`. */
export function linkButtonLabel({ title, url, linkType }: LinkButtonLabelInput): LinkButtonLabel {
  const text = (title ?? '').trim();
  if (text !== '') {
    return { kind: 'title', text };
  }

  const host = linkHost(url);
  const typed = linkType !== null && linkType !== undefined && isLinkType(linkType) ? linkType : null;
  // A known service type wins. A generic or missing type (or a social type with a service url,
  // which an older wizard could store) falls back to the address.
  const serviceType =
    (typed !== null && SERVICE_NAMES[typed] !== undefined ? typed : null) ?? serviceLinkTypeForUrl(url);
  const serviceName = serviceType === null ? undefined : SERVICE_NAMES[serviceType];
  if (serviceType !== null && serviceName !== undefined) {
    return { kind: 'service', verb: VIDEO_SERVICES.includes(serviceType) ? 'watch' : 'listen', serviceName };
  }

  if (host !== null) {
    return { kind: 'host', host };
  }
  return { kind: 'none' };
}
